import { Controller } from "@hotwired/stimulus";
import DOMPurify from "dompurify";
import { customMatcher, domFromHtml } from "../utils/select2_utils";

export default class extends Controller {
  static values = {
    type: { type: String, default: "select" }, // "select" or "tags"
    noResultsMsg: { type: String, default: "No results found" }
  };

  connect() {
    // Stimulus reconnects when Turbo replaces cached DOM, so guard to avoid
    // creating duplicate Select2 wrappers if the previous instance wasn't fully cleaned up.
    this.initializeSelect2();
  }

  disconnect() {
    // Turbo caches the raw form markup, so tear everything down to leave
    // a pristine <select> behind (otherwise back/forward visits show duplicate containers).
    this.destroySelect2();
  }

  initializeSelect2() {
    if (this.isSelect2Initialized(this.element)) return;

    // Turbo restores cached markup before we reconnect, so scrub any orphaned
    // Select2 DOM/classes before we attempt to re-initialize.
    this.removeStaleSelect2Artifacts();

    const el = this.element;
    const elementOptions = el.dataset.options ? JSON.parse(el.dataset.options) : el.dataset;
    const defaultOptions = this.getDefaultOptions(el);
    const options = { ...elementOptions, ...defaultOptions };

    $(el).select2(options);

    // Fire a real change event so Rails helpers/stimulus targets pick up value changes.
    this.nativeChangeHandler = () => {
      const event = new Event("change", { bubbles: true });
      this.element.dispatchEvent(event);
    };
    $(el).on("select2:select select2:clear select2:unselect", this.nativeChangeHandler);

    this.handleSelectBound = this.handleSelect.bind(this);
    $(el).on("select2:select", this.handleSelectBound);
  }

  destroySelect2() {
    if (!this.isSelect2Initialized(this.element)) return;

    const el = $(this.element);
    if (this.nativeChangeHandler) {
      el.off("select2:select select2:clear select2:unselect", this.nativeChangeHandler);
      this.nativeChangeHandler = null;
    }
    if (this.handleSelectBound) {
      el.off("select2:select", this.handleSelectBound);
      this.handleSelectBound = null;
    }
    el.select2("destroy");
  }

  removeStaleSelect2Artifacts() {
    // Cached pages can leave the previous Select2 container in the DOM, which confuses
    // the next initialization unless we remove it and reset the original <select>.
    const sibling = this.element.nextElementSibling;
    if (sibling && sibling.classList?.contains("select2")) {
      sibling.remove();
    }
    this.element.classList.remove("select2-hidden-accessible");
    this.element.removeAttribute("data-select2-id");
    $(this.element).off(".select2");
  }

  isSelect2Initialized(el) {
    return Boolean($(el).data("select2"));
  }

  getDefaultOptions(el) {
    let options = {
      minimumResultsForSearch: 5,
      templateResult: (item) => {
        // Given an item, determine if html has been provided, otherwise fall back to a standard template
        if (item.loading || item.id === "") return item.text;

        const html = item.result_html || item.element?.dataset?.resultHtml || standardResultTemplate(item);
        return domFromHtml(html, ["span", "i", "kbd", "small", "div", "br", "strong", "img"]);
      },
      templateSelection: (item) => {
        // Given an item, determine if html has been provided, otherwise fall back to a standard template
        if (item.loading || item.id === "") return item.text;

        const html = item.selection_html || item.element?.dataset?.selectionHtml || standardSelectionTemplate(item);
        return domFromHtml(html, ["span", "i", "small"]);
      },
      language: {
        noResults: () => {
          return DOMPurify.sanitize(this.noResultsMsgValue);
        },
      },
      matcher: customMatcher,
    };

    if (this.typeValue === "tags") {
      options.tags = true;
      options.minimumResultsForSearch = 0;
      options.multiple = el.multiple;
      options.tokenSeparators = [";"];

      if (!el.multiple) {
        options.allowClear = true;
      }
    }

    return options;
  }

  handleSelect() {
    if (this.element.dataset.closeOnSelect === "false") {
      this.clearSearchInput();
    }
  }

  clearSearchInput() {
    const searchInput = this.element.nextSibling.querySelector(".select2-search__field");

    if (searchInput !== undefined && searchInput !== null) {
      searchInput.value = " ";
    }
  }
}

// Standard selection template for select2 items
function standardSelectionTemplate(item) {
  const ds = item.element?.dataset || {};
  const icon = item.icon ?? ds.icon;
  const text = item.text ?? "";

  const iconHtml = icon ? `<i class="${icon} mr-3" aria-hidden="true"></i>` : "";
  const html = `<span>${iconHtml} ${text}</span>`;
  return html
}

// Standard result template for select2 items
function standardResultTemplate(item) {
  const ds = item.element?.dataset || {};
  const icon = item.icon ?? ds.icon;
  const subtitle = item.subtitle ?? ds.subtitle;
  const text = item.text ?? "";

  // if neither icon nor subtitle provided, let caller fall back to plain text
  if (!icon && !subtitle) {
    return `<div>${text}</div>`;
  }

  const iconHtml = icon ? `<i class="${icon} mr-3" aria-hidden="true"></i>` : "";
  const subtitleHtml = subtitle ? `<small class="d-block text-muted">${subtitle}</small>` : "";

  const html = `
    <div class="d-flex align-items-baseline">
      ${iconHtml}
      <div>
        ${text}
        ${subtitleHtml}
      </div>
    </div>
  `;

  return html;
}
